import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
} from '@mui/material';
import { AppButton, AppTextField } from '../../../components/design-system';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import { opsDevicesService } from '../../../services/OpsDevicesService';

interface UnassignModalProps {
  open: boolean;
  serialNumber: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UnassignModal({
  open,
  serialNumber,
  onClose,
  onSuccess,
}: UnassignModalProps) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setReason('');
    setConfirmed(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    if (!confirmed) {
      setError('Please confirm the action by checking the box.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await opsDevicesService.unallocateDevice(serialNumber, reason.trim());
      onSuccess();
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unassign failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        Unassign Device
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Serial:</strong> {serialNumber}
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          This will release a <strong>pre-shipment</strong> allocation only:
          <Box component="ul" sx={{ marginTop: '4px', paddingLeft: '20px', margin: '4px 0 0 0', pl: '20px' }}>
            <li>Remove the device from its current tenant and order</li>
            <li>Move inventory status from <strong>Allocated</strong> to <strong>In Stock</strong></li>
            <li>Make the device available for reassignment to other tenants</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            For shipped or claimed devices, use <strong>Reset for Redeployment</strong> instead.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <AppTextField
          label="Reason for Unassigning *"
          fullWidth
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., Order cancelled, Project completed, or Incorrect allocation"
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          }
          label="I confirm that I want to return this device to general inventory."
        />
      </DialogContent>
      <DialogActions>
        <AppButton onClick={handleClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={submitting || !confirmed || !reason.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Unassign Device
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
